import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api, downloadBlob } from '../../api/client'
import type { AccessEvento, AccessFiltri } from '../../api/types'
import { t } from '../../i18n/lingua'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import { DataTable, type Column } from '../DataTable'
import { BadgeEvento, etichettaAzione, etichettaFonte, etichettaTipoAccesso } from './comuni'

/**
 * «Accessi»: the register, newest first. The filters that matter are asked of
 * the server (period, privileged, off hours, outcome, source, text): the
 * register is millions of rows, the table shows the page that came back and
 * «Carica altri» asks the next one. The filters live in the address, so a
 * number of the overview opens the list already filtered. Every search and
 * every export is itself recorded (Registro dell'area).
 */

const PERIODI: { key: string; label: () => string; ore: number }[] = [
  { key: '24h', label: () => t('ultime 24 ore'), ore: 24 },
  { key: '7d', label: () => t('ultimi 7 giorni'), ore: 24 * 7 },
  { key: '30d', label: () => t('ultimi 30 giorni'), ore: 24 * 30 },
  { key: '90d', label: () => t('ultimi 90 giorni'), ore: 24 * 90 },
  { key: 'all', label: () => t('tutto il registro'), ore: 0 },
]

export function filtriDaIndirizzo(p: URLSearchParams): AccessFiltri {
  const periodo = PERIODI.find((x) => x.key === (p.get('periodo') ?? '7d')) ?? PERIODI[1]
  const booleano = (k: string) => (p.get(k) === '1' ? true : p.get(k) === '0' ? false : undefined)
  return {
    since: periodo.ore ? new Date(Date.now() - periodo.ore * 3600_000).toISOString() : undefined,
    privileged: booleano('privilegiati'),
    off_hours: booleano('fuori'),
    outcome: p.get('esito') ?? undefined,
    source_kind: p.get('fonte') ?? undefined,
    action: p.get('azione') ?? undefined,
    identity_id: p.get('persona') ?? undefined,
    ci_id: p.get('ci') ?? undefined,
    q: p.get('q') ?? undefined,
  }
}

export function colonneEventi(conPersona = true): Column<AccessEvento>[] {
  return [
    { key: 'ts', header: t('Quando'), type: 'date', value: (e) => e.ts, render: (e) => formatDateTime(e.ts), nowrap: true },
    { key: 'esito', header: t('Esito'), value: (e) => `${e.outcome} ${e.privileged ? 'privileged' : ''} ${e.off_hours ? 'off_hours' : ''}`, render: (e) => <BadgeEvento e={e} />, filter: false },
    { key: 'action', header: t('Azione'), value: (e) => etichettaAzione(e.action), filter: 'select' },
    ...(conPersona ? [{
      key: 'account', header: t('Chi'), value: (e: AccessEvento) => e.identity_name ?? e.account ?? '',
      render: (e: AccessEvento) => e.account
        ? (e.identity_id ? <Link to={`/access/people/${e.identity_id}`}>{e.account}</Link> : e.account)
        : <span className="muted" title={t('Account non privilegiato: con gli utenti non privilegiati spenti si conserva senza il nome.')}>{t('(non privilegiato)')}</span>,
    }] : []),
    { key: 'host', header: t('Dove'), value: (e) => e.ci_name ?? e.host ?? '', render: (e) => (
      <>{e.ci_id ? <Link to={`/access/events?ci=${e.ci_id}`}>{e.ci_name ?? e.host}</Link> : (e.host ?? '—')}
        {(e.ci_ip || e.ci_class) && <><br /><small className="muted">{[e.ci_ip, e.ci_class_label ? t(e.ci_class_label) : e.ci_class].filter(Boolean).join(' · ')}</small></>}</>
    ) },
    { key: 'ci_ip', header: t('IP del sistema'), mono: true, value: (e) => e.ci_ip ?? '', hidden: true },
    { key: 'logon_type', header: t('Come'), value: (e) => etichettaTipoAccesso(e.logon_type), filter: 'select' },
    { key: 'src', header: t('Da'), mono: true, value: (e) => [e.src_ip, e.src_host, e.country].filter(Boolean).join(' '), render: (e) => (
      <>{e.src_ip ?? '—'}{(e.src_host || e.country) && <><br /><small className="muted">{[e.src_host, e.country].filter(Boolean).join(' · ')}</small></>}</>
    ) },
    { key: 'source_kind', header: t('Fonte'), value: (e) => etichettaFonte(e.source_kind), filter: 'select' },
    { key: 'app', header: t('Applicazione'), value: (e) => e.app ?? '', hidden: true },
    { key: 'code', header: t('Codice'), mono: true, value: (e) => e.code ?? '', hidden: true },
    { key: 'seq', header: t('N.'), type: 'number', value: (e) => e.seq, hidden: true },
    { key: 'hash', header: t('Impronta'), mono: true, value: (e) => e.hash.slice(0, 16), hidden: true },
  ]
}

export function VistaEventi() {
  const [parametri, setParametri] = useSearchParams()
  const filtri = useMemo(() => filtriDaIndirizzo(parametri), [parametri])
  const [righe, setRighe] = useState<AccessEvento[]>([])
  const [prossima, setProssima] = useState<number | null>(null)
  const [carica, setCarica] = useState(true)
  const [errore, setErrore] = useState<unknown>(null)
  const [testo, setTesto] = useState(parametri.get('q') ?? '')
  const chiave = parametri.toString()

  useEffect(() => {
    let vivo = true
    setCarica(true)
    setErrore(null)
    api.accessEvents(filtri).then((r) => {
      if (!vivo) return
      setRighe(r.events)
      setProssima(r.next_before)
    }).catch((e) => vivo && setErrore(e)).finally(() => vivo && setCarica(false))
    return () => { vivo = false }
  }, [chiave]) // eslint-disable-line react-hooks/exhaustive-deps

  async function altri() {
    if (!prossima) return
    setCarica(true)
    try {
      const r = await api.accessEvents(filtri, prossima)
      setRighe((x) => [...x, ...r.events])
      setProssima(r.next_before)
    } catch (e) {
      setErrore(e)
    } finally {
      setCarica(false)
    }
  }
  function imposta(k: string, v: string | null) {
    const q = new URLSearchParams(parametri)
    if (v === null || v === '') q.delete(k)
    else q.set(k, v)
    setParametri(q, { replace: true })
  }
  async function esporta() {
    try {
      const blob = await downloadBlob(api.accessEventsExportPath(filtri))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `censo-access-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (e) {
      setErrore(e)
    }
  }

  const tre = (k: string, etichette: [string, string, string]) => (
    <select value={parametri.get(k) ?? ''} onChange={(e) => imposta(k, e.target.value || null)} aria-label={etichette[0]}>
      <option value="">{etichette[0]}</option>
      <option value="1">{etichette[1]}</option>
      <option value="0">{etichette[2]}</option>
    </select>
  )

  return (
    <Card title={t('Registro degli accessi')}>
      <div className="accfiltri">
        <select value={parametri.get('periodo') ?? '7d'} onChange={(e) => imposta('periodo', e.target.value)} aria-label={t('Periodo')}>
          {PERIODI.map((p) => <option key={p.key} value={p.key}>{p.label()}</option>)}
        </select>
        {tre('privilegiati', [t('privilegiati e non'), t('solo privilegiati'), t('solo non privilegiati')])}
        {tre('fuori', [t('in orario e fuori orario'), t('solo fuori orario'), t('solo in orario')])}
        <select value={parametri.get('esito') ?? ''} onChange={(e) => imposta('esito', e.target.value || null)} aria-label={t('Esito')}>
          <option value="">{t('riusciti e falliti')}</option>
          <option value="success">{t('solo riusciti')}</option>
          <option value="failure">{t('solo falliti')}</option>
        </select>
        <select value={parametri.get('fonte') ?? ''} onChange={(e) => imposta('fonte', e.target.value || null)} aria-label={t('Fonte')}>
          <option value="">{t('tutte le fonti')}</option>
          {['windows', 'dc', 'linux', 'entra', 'azure', 'network', 'wifi', 'censo'].map((f) => <option key={f} value={f}>{etichettaFonte(f)}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); imposta('q', testo.trim() || null) }} style={{ display: 'inline-flex', gap: 6 }}>
          <input type="search" value={testo} onChange={(e) => setTesto(e.target.value)} placeholder={t('account, sistema, indirizzo…')} aria-label={t('Cerca nel registro')} />
          <button type="submit" className="secondary">{t('Cerca')}</button>
        </form>
        {(parametri.get('persona') || parametri.get('ci')) && (
          <button type="button" className="linklike" onClick={() => { imposta('persona', null); imposta('ci', null) }}>{t('togli il filtro su persona o sistema')}</button>
        )}
        <button type="button" className="secondary" onClick={() => void esporta()} style={{ marginLeft: 'auto' }}>{t('Esporta CSV')}</button>
      </div>
      <ErrorBanner error={errore} />
      {carica && righe.length === 0 ? <Spinner /> : righe.length === 0 ? (
        <EmptyState title={t('Nessun evento con questi filtri.')} hint={t('Gli eventi arrivano dalle fonti accese (agenti, domain controller, sonde, Entra ID). Senza la dichiarazione per gli utenti non privilegiati si registrano solo gli accessi privilegiati e i tentativi falliti.')} />
      ) : (
        <>
          <DataTable rows={righe} columns={colonneEventi()} rowKey={(e) => String(e.seq)} conteggio={{ nome: t('eventi') }}
                     rowClassName={(e) => (e.outcome === 'failure' ? 'accriga--ko' : e.off_hours && e.privileged ? 'accriga--fuori' : undefined)} />
          {prossima && (
            <p style={{ textAlign: 'center' }}>
              <button type="button" className="secondary" onClick={() => void altri()} disabled={carica}>{carica ? t('Carico…') : t('Carica altri')}</button>
            </p>
          )}
        </>
      )}
    </Card>
  )
}
